import { h, Component } from 'preact';
import { observer } from 'mobx-preact';

// Header aliases for auto-detecting columns in pasted spreadsheet data.
const COLUMN_ALIASES = {
    firstname: ['fornavn', 'firstname', 'first name', 'given name'],
    lastname: ['etternavn', 'lastname', 'last name', 'surname'],
    name: ['navn', 'name', 'fullt navn', 'full name'],
    cellphone: ['telefon', 'mobil', 'mobiltelefon', 'telefonnummer', 'tlf', 'phone', 'cellphone', 'mobile'],
    email: ['e-post', 'epost', 'e-postadresse', 'e-mail', 'email', 'mail'],
    function: ['funksjon', 'rolle', 'oppgave', 'verv', 'function', 'role'],
    position: ['standplass', 'st.plass', 'stplass', 'plass', 'stand', 'lag', 'bane', 'position', 'posisjon'],
};

// Pick the most frequent of tab / semicolon / comma as the column delimiter.
function detectDelimiter(line) {
    const counts = {
        '\t': (line.match(/\t/g) || []).length,
        ';': (line.match(/;/g) || []).length,
        ',': (line.match(/,/g) || []).length,
    };
    const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return best && best[1] > 0 ? best[0] : '\t';
}

@observer
class OrganizersPasteImport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            open: false,
            csvText: '',
            parsed: null,
            error: null,
            importing: false,
            result: null,
        };
    }

    toggleOpen = () => {
        this.setState(prev => ({ open: !prev.open, error: null, result: null }));
    }

    handleInput = (e) => {
        this.setState({ csvText: e.target.value, parsed: null, error: null, result: null });
    }

    clear = () => {
        this.setState({ csvText: '', parsed: null, error: null, result: null });
    }

    parse = () => {
        const { csvText } = this.state;
        const { conventionStore } = this.props.stores;
        const lines = (csvText || '').replace(/\r/g, '').split('\n').filter(l => l.trim().length > 0);
        if (lines.length < 2) {
            this.setState({ error: 'Lim inn en overskriftsrad og minst én datarad.', parsed: null });
            return;
        }
        const delimiter = detectDelimiter(lines[0]);
        const splitRow = l => l.split(delimiter).map(c => c.trim().replace(/^﻿/, ''));
        const rawHeaders = splitRow(lines[0]);
        const headers = rawHeaders.map(h => h.toLowerCase());

        // Map field -> column index (two-pass: exact match, then substring).
        const mapping = {};
        const used = new Set();
        const runPass = (matches) => {
            Object.entries(COLUMN_ALIASES).forEach(([field, aliases]) => {
                if (mapping[field] != null) return;
                for (let i = 0; i < headers.length; i += 1) {
                    if (used.has(i)) continue;
                    if (matches(headers[i], aliases)) {
                        mapping[field] = i;
                        used.add(i);
                        break;
                    }
                }
            });
        };
        runPass((h, aliases) => aliases.includes(h));
        runPass((h, aliases) => aliases.some(a => h.includes(a)));

        if (mapping.firstname == null && mapping.lastname == null && mapping.name == null) {
            this.setState({
                error: 'Fant ingen navne-kolonne. Legg til en overskriftsrad, f.eks. «Navn, Telefon, E-post, Funksjon».',
                parsed: null,
            });
            return;
        }

        const existing = conventionStore.convention?.organizers || [];
        const seenEmails = new Set(existing.filter(o => o.email).map(o => String(o.email).toLowerCase().trim()));
        const seenPhones = new Set(existing.filter(o => o.cellphone).map(o => String(o.cellphone).replace(/\s/g, '')));

        const parsed = [];
        for (let i = 1; i < lines.length; i += 1) {
            const cols = splitRow(lines[i]);
            const get = field => (mapping[field] != null ? (cols[mapping[field]] || '') : '');

            // "(SPL)" in the name marks a standplassleder (stand leader). Detect it,
            // then strip it (and any padding spaces) out of the name itself.
            const splRe = /\(\s*spl\s*\)/i;
            const rawFirst = get('firstname');
            const rawLast = get('lastname');
            const rawName = get('name');
            const isSpl = splRe.test(rawName) || splRe.test(rawFirst) || splRe.test(rawLast);
            const cleanName = s => s.replace(/\(\s*spl\s*\)/ig, '').replace(/\s+/g, ' ').trim();
            let firstname = cleanName(rawFirst);
            let lastname = cleanName(rawLast);
            const fullName = cleanName(rawName);
            if (!firstname && !lastname && fullName) {
                const parts = fullName.split(/\s+/);
                firstname = parts.shift() || '';
                lastname = parts.join(' ');
            }
            const cellphone = get('cellphone');
            const email = get('email');

            // Function: use an explicit function column if present, otherwise derive
            // it from the stand/Standplass column. SPL gets a "(SPL)" suffix.
            let fn = get('function').trim();
            const position = get('position').trim();
            if (!fn && position) {
                const rawLabel = (mapping.position != null && rawHeaders[mapping.position]) || 'Standplass';
                const label = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
                fn = `${label} ${position}`;
            }
            if (isSpl) {
                fn = fn ? `${fn} (SPL)` : 'SPL';
            }
            if (!firstname && !lastname && !cellphone && !email) continue;

            const emailKey = email.toLowerCase().trim();
            const phoneKey = cellphone.replace(/\s/g, '');
            let status = 'new';
            if ((emailKey && seenEmails.has(emailKey)) || (phoneKey && seenPhones.has(phoneKey))) {
                status = 'dup';
            } else {
                if (emailKey) seenEmails.add(emailKey);
                if (phoneKey) seenPhones.add(phoneKey);
            }
            parsed.push({ firstname, lastname, cellphone, email, function: fn, status });
        }

        if (parsed.length === 0) {
            this.setState({ error: 'Fant ingen gyldige rader.', parsed: null });
            return;
        }
        this.setState({ parsed, error: null, result: null });
    }

    doImport = async () => {
        const { parsed } = this.state;
        const { conventionStore, appState } = this.props.stores;
        const { onImported } = this.props;
        const toAdd = (parsed || []).filter(p => p.status === 'new');
        if (toAdd.length === 0) return;

        this.setState({ importing: true });
        let result;
        try {
            result = await conventionStore.addOrganizersFromList(toAdd);
        } catch (err) {
            appState.addToast({ type: 'error', message: err.message || 'Import feilet' }, 5000);
            this.setState({ importing: false });
            return;
        }
        this.setState({ importing: false, result, parsed: null, csvText: '' });
        appState.addToast({ type: 'success', message: `La til ${result.added} arrangører` }, 4000);
        if (onImported) await onImported();
    }

    render() {
        const { open, csvText, parsed, error, importing, result } = this.state;
        const newCount = (parsed || []).filter(p => p.status === 'new').length;
        const dupCount = (parsed || []).filter(p => p.status === 'dup').length;

        return (
            <div class='mb-3'>
                <button
                    type='button'
                    class='btn btn-sm btn-outline-secondary rounded-pill'
                    onClick={this.toggleOpen}
                >
                    <i class={`fa-solid fa-chevron-${open ? 'down' : 'right'} me-1`} />
                    <i class='fa-duotone fa-file-import me-1' />
                    Lim inn liste (Excel / Google Sheets)
                </button>

                {open && (
                    <div class='mt-2'>
                        <textarea
                            class='form-control font-monospace'
                            rows={6}
                            placeholder={'Dato\tStandplass\tNavn\tMobil\tStatus\n31.05.2026\t1\tOla Nordmann (SPL)\t99887766\tx\n31.05.2026\t1\tKari Hansen\t90011223\tx'}
                            value={csvText}
                            onInput={this.handleInput}
                        />
                        <small class='text-body-secondary d-block mt-1'>
                            Lim inn med overskriftsrad. Støtter tab-, semikolon- og komma-separert. Kolonner: Navn (eller Fornavn/Etternavn), Telefon/Mobil, E-post, Funksjon, Standplass. En «Standplass»-kolonne blir til funksjon (f.eks. «Standplass 1»), og «(SPL)» i navnet markerer standplassleder. Kolonnene «Dato» og «Status» ignoreres.
                        </small>

                        {error && (
                            <div class='alert alert-warning mt-2 mb-0 py-2'>{error}</div>
                        )}

                        <div class='d-flex gap-2 mt-2'>
                            <button
                                type='button'
                                class='btn btn-sm btn-primary rounded-pill'
                                onClick={this.parse}
                                disabled={!csvText.trim() || importing}
                            >
                                <i class='fa-solid fa-eye me-1' />Forhåndsvis
                            </button>
                            {csvText && (
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-secondary rounded-pill'
                                    onClick={this.clear}
                                    disabled={importing}
                                >
                                    Tøm
                                </button>
                            )}
                        </div>

                        {parsed && parsed.length > 0 && (
                            <div class='mt-2'>
                                <div class='small mb-1'>
                                    <span class='badge text-bg-success me-1'>{newCount} nye</span>
                                    {dupCount > 0 && <span class='badge text-bg-secondary'>{dupCount} dublett</span>}
                                </div>
                                <div class='table-responsive' style='max-height: 260px; overflow-y: auto;'>
                                    <table class='table table-sm table-striped'>
                                        <thead>
                                            <tr>
                                                <th>Navn</th>
                                                <th>Telefon</th>
                                                <th>E-post</th>
                                                <th>Funksjon</th>
                                                <th>&nbsp;</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {parsed.map((p, idx) => (
                                                <tr key={idx} class={p.status === 'dup' ? 'opacity-50' : ''}>
                                                    <td><nobr>{p.firstname} {p.lastname}</nobr></td>
                                                    <td><nobr>{p.cellphone}</nobr></td>
                                                    <td>{p.email}</td>
                                                    <td>{p.function}</td>
                                                    <td>
                                                        {p.status === 'dup'
                                                            ? <span class='badge text-bg-secondary'>finnes</span>
                                                            : <span class='badge text-bg-success'>ny</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <button
                                    type='button'
                                    class='btn btn-success rounded-pill'
                                    onClick={this.doImport}
                                    disabled={importing || newCount === 0}
                                >
                                    {importing
                                        ? <i class='fa-duotone fa-spinner-third fa-spin me-1' />
                                        : <i class='fa-solid fa-plus me-1' />}
                                    Importer {newCount} arrangører
                                </button>
                            </div>
                        )}

                        {result && (
                            <div class='alert alert-success mt-2 mb-0 py-2'>
                                La til {result.added} arrangører{result.skipped > 0 ? `, hoppet over ${result.skipped} dubletter` : ''}.
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default OrganizersPasteImport;
